import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  BarChart3, TrendingUp, Users, Video, DollarSign,
  Calendar, Download, Mail, FileText
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

export default function AdminAnalyticsPage() {
  const { token } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [userChartData, setUserChartData] = useState([]);
  const [streamChartData, setStreamChartData] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usersRes, streamsRes, revenueRes, statsRes] = await Promise.all([
        axios.get(`${API}/dashboard/charts/users?period=${period}`, { headers }),
        axios.get(`${API}/dashboard/charts/streams?period=${period}`, { headers }),
        axios.get(`${API}/dashboard/charts/revenue?period=${period}`, { headers }),
        axios.get(`${API}/dashboard/stats`, { headers }),
      ]);

      setUserChartData(usersRes.data.data || []);
      setStreamChartData(streamsRes.data.data || []);
      setRevenueChartData(revenueRes.data.data || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b'];

  // Calculate totals for pie chart
  const pieData = [
    { name: 'Platform (40%)', value: stats?.overview?.totalRevenue * 0.4 || 0 },
    { name: 'Creators (60%)', value: stats?.overview?.totalRevenue * 0.6 || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Analytics</h1>
          <p className="text-zinc-500 text-sm mt-1">Platform performance insights</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          
          <button className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500/20 text-indigo-400 rounded-xl hover:bg-indigo-500/30 transition-all">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-400" />
            </div>
            <span className="text-zinc-400 text-sm">Total Users</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.overview?.totalUsers?.toLocaleString() || 0}</p>
          <p className="text-emerald-400 text-sm mt-2">+{stats?.thisWeek?.newUsers || 0} this week</p>
        </div>
        
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-zinc-400 text-sm">Total Streams</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.overview?.totalStreams || 0}</p>
          <p className="text-emerald-400 text-sm mt-2">+{stats?.today?.newStreams || 0} today</p>
        </div>
        
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-zinc-400 text-sm">Total Revenue</span>
          </div>
          <p className="text-3xl font-bold text-amber-400">{stats?.overview?.totalRevenue?.toFixed(2) || '0.00'}</p>
          <p className="text-zinc-500 text-sm mt-2">tokens earned</p>
        </div>
        
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-zinc-400 text-sm">Platform Share</span>
          </div>
          <p className="text-3xl font-bold text-emerald-400">{(stats?.overview?.totalRevenue * 0.4)?.toFixed(2) || '0.00'}</p>
          <p className="text-zinc-500 text-sm mt-2">40% of revenue</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* User Growth */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <h3 className="text-white font-semibold mb-6" style={{ fontFamily: 'Manrope' }}>User Growth</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userChartData}>
                <defs>
                  <linearGradient id="userGradient2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 11 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#userGradient2)" name="New Users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stream Activity */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <h3 className="text-white font-semibold mb-6" style={{ fontFamily: 'Manrope' }}>Stream Activity</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={streamChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 11 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Bar dataKey="count" fill="#8b5cf6" name="Streams" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" fill="#10b981" name="Completed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <h3 className="text-white font-semibold mb-6" style={{ fontFamily: 'Manrope' }}>Revenue Over Time</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 11 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} dot={false} name="Total Revenue" />
                <Line type="monotone" dataKey="platform_share" stroke="#6366f1" strokeWidth={2} dot={false} name="Platform Share" />
                <Line type="monotone" dataKey="creator_share" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Creator Share" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Distribution */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <h3 className="text-white font-semibold mb-6" style={{ fontFamily: 'Manrope' }}>Revenue Distribution</h3>
          <div className="h-72 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff' }}
                  formatter={(value) => value.toFixed(2) + ' tokens'}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Report generation section */}
      <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
        <h3 className="text-white font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Generate Reports</h3>
        <p className="text-zinc-500 text-sm mb-6">Export detailed reports for the selected period</p>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 hover:border-indigo-500/50 transition-all group">
            <FileText className="w-8 h-8 text-indigo-400 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <p className="text-white font-medium">User Report</p>
              <p className="text-zinc-500 text-xs">Export as PDF</p>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 hover:border-purple-500/50 transition-all group">
            <FileText className="w-8 h-8 text-purple-400 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <p className="text-white font-medium">Stream Report</p>
              <p className="text-zinc-500 text-xs">Export as PDF</p>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 hover:border-amber-500/50 transition-all group">
            <FileText className="w-8 h-8 text-amber-400 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <p className="text-white font-medium">Revenue Report</p>
              <p className="text-zinc-500 text-xs">Export as PDF</p>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 hover:border-emerald-500/50 transition-all group">
            <Mail className="w-8 h-8 text-emerald-400 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <p className="text-white font-medium">Email Report</p>
              <p className="text-zinc-500 text-xs">Send to admin</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
